import { useFormik } from 'formik';
import { omit } from 'lodash-es';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import Alert from 'react-bootstrap/Alert';
import { Spinner } from 'react-bootstrap';
import Button from 'react-bootstrap/Button';
import Card from 'react-bootstrap/Card';
import Col from 'react-bootstrap/Col';
import Form from 'react-bootstrap/Form';
import Row from 'react-bootstrap/Row';
import * as yup from 'yup';
import { ButtonAction, Event } from '../../../../../dorian-shared/types/event/Event';
import { DateTimeField } from '../../../../ui/_fields/DateTimeField/DateTimeField';
import { NumberField } from '../../../../ui/_fields/NumberField/NumberField';
import { TextareaFieldInput } from '../../../../ui/_fields/TextareaFieldInput/TextareaFieldInput';
import { TextField } from '../../../../ui/_fields/TextField/TextField';
import { Variant } from '../../../../../dorian-shared/components/ImageCropper/ImageCropper';
import { BadgeWithTooltip } from '../../../../ui/AvatarTool/BadgeWithTooltip/BadgeWithTooltip';
import { ConfirmModal } from '../../../../ui/ConfirmModal/ConfirmModal';
import { FileInputValue, ImageInput } from '../ImageInput/ImageInput';
import { RingColorPicker } from '../RingColorPicker/RingColorPicker';
import { ButtonTargetInput } from './ButtonTargetInput/ButtonTargetInput';
import {
  CONTEST_TRIGGER_MAX,
  ContestDeliveryType,
  ContestRewardType,
  ContestTriggerFormValues,
  ContestTriggerSection,
  type ContestTriggerActiveSchema,
  contestApiToFormValues,
  createEmptyContestTriggerValues,
  isContestTriggerPopulated,
} from './ContestTrigger/ContestTriggerSection';
import {
  CONTEST_TRIGGER_V2_MAX,
  ContestTriggerV2EpisodeCard,
  ContestTriggerV2FormValues,
  buildAmountsJsonFromV2Form,
  buildTriggerDataFromV2Episodes,
  createEmptyContestTriggerV2Values,
  isContestTriggerV2Populated,
  v2RewardFieldsFromApiAmounts,
} from './ContestTrigger/contestTriggerV2Types';
import { prizeLabelMaxLength } from './DetailedPrizes/DetailedPrizeCard/DetailedPrizeCard';
import { DetailedPrizes, DetailedPrizesProps } from './DetailedPrizes/DetailedPrizes';
import { DetailedPrize } from './DetailedPrizes/DetailedPrizeType';

export enum PrizeType {
  Hearts = 'hearts',
  Sapphires = 'sapphires',
  None = ''
}

// eslint-disable-next-line no-template-curly-in-string
const requiredMessage = '${path} is required';
// eslint-disable-next-line no-template-curly-in-string
const maxMessage = '${path} should be up to ${max} characters';
// eslint-disable-next-line no-template-curly-in-string
const minMessage = '${path} should be at least ${min} characters';
const maxPrizeValueLength = 50;

function getEarliestEndTime(startTime: Date) {
  return moment(startTime).add(1, 'day').toDate();
}

const titleMaxLength = 50;
const descriptionMaxLength = 10000;
const labelMaxLength = 40;
const buttonTextMaxLength = 20;
const colorsMaxLength = 250;
const specialRequirementsMaxLength = 10000;
const authorLabelMaxLength = 50;

const uuidWithDashesRegex = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;

/** Which contest trigger schema is persisted; only one may exist per event (aux v1 vs contests_v2). */
export function inferContestTriggerActiveSchema(
  v1: ContestTriggerFormValues,
  v2: ContestTriggerV2FormValues,
): ContestTriggerActiveSchema {
  if (isContestTriggerV2Populated(v2)) return 'v2';
  if (isContestTriggerPopulated(v1)) return 'v1';
  return 'v1';
}

const contestTriggerV1Yup = yup.object().shape({
  triggerKey: yup.string().label('Trigger').max(CONTEST_TRIGGER_MAX.trigger, maxMessage),
  episodeUuid: yup.string().label('Episode UUID').test(
    'uuid-with-dashes',
    'Must be a valid UUID with dashes (e.g. xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx)',
    (val) => {
      const v = (val ?? '').trim();
      if (!v) return true;
      return uuidWithDashesRegex.test(v);
    },
  ),
  rewardType: yup.string().oneOf([ContestRewardType.Sapphires, ContestRewardType.Hearts]),
  rewardAmount: yup.number().label('Reward amount').min(0).integer('Only whole numbers are allowed'),
  deliveryType: yup.string().oneOf([ContestDeliveryType.Immediate]),
  title: yup.string().label('Contest title').max(CONTEST_TRIGGER_MAX.title, maxMessage),
  message: yup.string().label('Contest message').max(CONTEST_TRIGGER_MAX.message, maxMessage),
});

const contestTriggerV2Yup = yup.object({
  triggerIdentifier: yup.string().label('Trigger identifier (V2)').max(CONTEST_TRIGGER_V2_MAX.triggerIdentifier, maxMessage),
  episodes: yup
    .array()
    .of(
      yup.object({
        id: yup.string(),
        episodeUuid: yup.string().label('Episode UUID').test(
          'uuid-with-dashes-v2-ep',
          'Must be a valid UUID with dashes (e.g. xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx)',
          (val) => {
            const v = (val ?? '').trim();
            if (!v) return true;
            return uuidWithDashesRegex.test(v);
          },
        ),
        branches: yup.array().of(
          yup.object({
            id: yup.string(),
            branchName: yup.string().label('Branch name').max(CONTEST_TRIGGER_V2_MAX.branchName, maxMessage),
            stepIndex: yup.number().label('Step index').integer('Only whole numbers are allowed').min(0),
          }),
        ).default([]),
      }),
    )
    .default([])
    .test(
      'contest-v2-unique-episode-uuids',
      'Each episode UUID can only appear once.',
      (episodes: ContestTriggerV2EpisodeCard[] | undefined) => {
        const list = episodes ?? [];
        const uuids = list.map((e) => String(e?.episodeUuid ?? '').trim()).filter(Boolean);
        return new Set(uuids).size === uuids.length;
      },
    )
    .test(
      'contest-v2-unique-branch-names',
      'Branch names must be unique within each episode.',
      (episodes: ContestTriggerV2EpisodeCard[] | undefined) => {
        for (const ep of episodes ?? []) {
          const names = (ep?.branches ?? []).map((b) => String(b?.branchName ?? '').trim()).filter(Boolean);
          if (new Set(names).size !== names.length) return false;
        }
        return true;
      },
    ),
  title: yup.string().label('Contest title (V2)').max(CONTEST_TRIGGER_V2_MAX.title, maxMessage),
  message: yup.string().label('Contest message (V2)').max(CONTEST_TRIGGER_V2_MAX.message, maxMessage),
  rewardType: yup.string().oneOf([ContestRewardType.Sapphires, ContestRewardType.Hearts]),
  rewardAmount: yup.number().label('Reward amount').min(0).integer('Only whole numbers are allowed'),
});

// Operator copy for the Event Shelf fields. Each entry must say what happens when the field is left empty.
const CARD_ICON_INFO = {
  title: 'Card Icon',
  content: 'Shows as an oval on the shelf. Sides and corners are clipped, so keep the subject in the middle. If left empty, the landscape banner gets cropped into that oval and usually looks wrong.',
} as const;

const MODAL_COVER_INFO = {
  title: 'Modal Cover',
  content: 'This is the whole popup background. The app draws nothing over it, so put any text in the image itself. If left empty, the popup shows the landscape banner, cropped to fill the tall frame.',
} as const;

const ringColorRegex = /^(#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6}))?$/;

const schema = yup.object().shape({
  title: yup.string().label('Title').max(titleMaxLength, maxMessage).required(requiredMessage),
  description: yup.string().label('Description').min(10, minMessage).max(descriptionMaxLength, maxMessage)
    .required(requiredMessage),
  label: yup.string().label('Label').max(labelMaxLength, maxMessage),
  imageUrl: yup.string().label('Image').required(requiredMessage),
  // The Event Shelf fields are optional; never make them required (legacy events have none).
  cardImageUrl: yup.string().label('Card Image'),
  detailImageUrl: yup.string().label('Modal Cover Image'),
  ringColor: yup.string().label('Ring Colour').matches(ringColorRegex, 'Ring colour must be #RGB or #RRGGBB'),
  specialRequirements: yup.string().label('Rules').min(10, minMessage).max(specialRequirementsMaxLength, maxMessage),
  authorLabel: yup.string().label('Author').max(authorLabelMaxLength, maxMessage),
  active: yup.bool(),
  priority: yup.number().label('Order').integer('Only whole numbers are allowed').min(1, minMessage)
    .required(requiredMessage),
  buttonText: yup.string().label('Button Text').max(buttonTextMaxLength, maxMessage).required(requiredMessage),
  buttonTarget: yup.string().label('Button Target').required(requiredMessage),
  prize: yup.object().shape({
    prizeType: yup.string(),
    prizeValue: yup.string(),
  }).test(
    'has-prize-value-if-hearts',
    "Prize value can't be empty if prize type selected",
    (prize) => (prize.prizeType === PrizeType.Hearts || prize.prizeType === PrizeType.Sapphires
      ? Boolean(prize.prizeValue)
      : true),
  ).test(
    'prize-value-is-too-long',
    `Prize Value should be up to ${maxPrizeValueLength}`,
    (prize) => {
      const length = prize.prizeValue?.length ?? 0;
      return length <= maxPrizeValueLength;
    },
  ),
  colors: yup.string().max(colorsMaxLength, maxMessage),
  startTime: yup.date().label('Start Time').required(requiredMessage),
  endTime: yup.date().label('End Time').required(requiredMessage).when(
    'startTime',
    (startTime, yupEndTime) => startTime && yupEndTime.min(getEarliestEndTime(startTime), 'Duration should be at least a day'),
  ),
  detailedEventPrizes: yup.array(yup.object().shape({
    label: yup.string().label('Event Prize`s label').min(10, minMessage).max(prizeLabelMaxLength, maxMessage)
      .required(requiredMessage),
    imageUrl: yup.string().label('Event Prize`s image').required(requiredMessage),
  })),
  contestTriggerActiveSchema: yup.string().oneOf(['v1', 'v2']).required(),
  contestTrigger: yup.object().when('contestTriggerActiveSchema', {
    is: 'v1',
    then: contestTriggerV1Yup,
    otherwise: yup.object(),
  }),
  contestTriggerV2: yup.object().when('contestTriggerActiveSchema', {
    is: 'v2',
    then: contestTriggerV2Yup,
    otherwise: yup.object(),
  }),
});

export interface EventCardPrize {
  prizeType: PrizeType
  prizeValue: string
}

function createEmptyCoverPrize() {
  return {
    prizeType: PrizeType.None,
    prizeValue: '',
  };
}

export interface EventFormValues extends Omit<Event, 'createdAt' | 'updatedAt' | 'id' | 'prize' | 'ringColor'> {
  id?: Event['id']
  prize: EventCardPrize | null
  newCoverImage?: FileInputValue
  /** Pending card icon file; sent on save. */
  newCardImage?: FileInputValue
  /** Pending modal cover file; sent on save. */
  newModalImage?: FileInputValue
  /**
   * Ring colour. Always a string in the form:
   *   '' means "no colour selected"
   *   '#RRGGBB' / '#RGB' means a colour is set
   * Overrides Event['ringColor'] (string | null) so the form never has to handle null.
   */
  ringColor: string
  /**
   * Ring colour intent tracker. Distinguishes:
   *   false (default) → operator never touched the ring colour section →
   *     FormData omits the field → server leaves the column unchanged.
   *   true → operator explicitly interacted (toggled or picked) →
   *     FormData always sends the current value, even if it is '' (which tells
   *     the server to CLEAR the column). This is the key distinction the API
   *     requires: absent = leave; '' = clear; hex = set.
   */
  ringColorTouched: boolean
  /**
   * Removal intent for the card icon.
   * false (default) → field absent from FormData → server leaves column unchanged.
   * true → send removeCardImage="1" → server deletes S3 object and clears cardImageObject.
   * When true, newCardImage MUST be undefined (conflict: upload + remove → 422).
   * The UI enforces mutual exclusion: the Remove handler clears newCardImage, and
   * the upload-confirm handler clears this flag.
   */
  removeCardImage: boolean
  /**
   * Removal intent for the modal cover.
   * Identical semantics to removeCardImage, applied to modalImageObject.
   */
  removeModalImage: boolean
  detailedEventPrizes: DetailedPrize[]
  /** UI + save: only this schema is sent to the API; the other is cleared in FormData. */
  contestTriggerActiveSchema: ContestTriggerActiveSchema
  contestTrigger: ContestTriggerFormValues
  contestTriggerV2: ContestTriggerV2FormValues
}

export function createInitialFormValues(): EventFormValues {
  return {
    title: '',
    description: '',
    imageUrl: '',
    active: false,
    priority: 1,
    buttonText: '',
    colors: '',
    label: '',
    startTime: '',
    endTime: '',
    prize: createEmptyCoverPrize(),
    buttonAction: ButtonAction.Story,
    buttonTarget: '',
    specialRequirements: '',
    authorLabel: '',
    detailedEventPrizes: [],
    contestTriggerActiveSchema: 'v1',
    contestTrigger: createEmptyContestTriggerValues(),
    contestTriggerV2: createEmptyContestTriggerV2Values(),
    ringColor: '',
    ringColorTouched: false,
    removeCardImage: false,
    removeModalImage: false,
  };
}

type NewOrExistingEvent = Omit<Event, 'createdAt' | 'updatedAt' | 'id'> & {
  id?: Event['id']
  createdAt?: Event['createdAt']
  updatedAt?: Event['updatedAt']
};

export function eventToFormValues(event: NewOrExistingEvent, detailedEventPrizes: DetailedPrize[]): EventFormValues {
  const defaults = createInitialFormValues();
  const raw = {
    ...omit(event, ['createdAt', 'updatedAt', 'prize', 'contest']),
    prize: event.prize ? JSON.parse(event.prize) : createEmptyCoverPrize(),
    detailedEventPrizes,
  };
  // API may return null for optional string fields — fall back to defaults
  const merged = Object.fromEntries(
    Object.entries(raw).map(([key, val]) => [
      key,
      val ?? (defaults as unknown as Record<string, unknown>)[key] ?? '',
    ]),
  ) as EventFormValues;
  const contestV2Defaults = createEmptyContestTriggerV2Values();
  const fromApi = event.contestV2;
  const contestTriggerV2: ContestTriggerV2FormValues = fromApi
    ? {
        ...contestV2Defaults,
        triggerIdentifier: fromApi.triggerIdentifier ?? '',
        title: fromApi.title ?? '',
        message: fromApi.message ?? '',
        episodes: Array.isArray(fromApi.episodes) && fromApi.episodes.length > 0
          ? fromApi.episodes
          : contestV2Defaults.episodes,
        ...v2RewardFieldsFromApiAmounts(fromApi.sapphiresAmount ?? 0, fromApi.heartsAmount ?? 0),
      }
    : contestV2Defaults;

  const contestTrigger = contestApiToFormValues(event.contest);

  return {
    ...merged,
    contestTrigger,
    contestTriggerV2,
    contestTriggerActiveSchema: inferContestTriggerActiveSchema(contestTrigger, contestTriggerV2),
    // Never carry over a touched state from a previous session; each load starts fresh
    ringColorTouched: false,
    removeCardImage: false,
    removeModalImage: false,
  };
}

export function createFormDataFromFormValues(values: Omit<EventFormValues, 'detailedEventPrizes'>) {
  const formData = new FormData();
  const active = values.contestTriggerActiveSchema;
  Object.entries(values).forEach(([key, value]) => {
    switch (key) {
      case 'contestTriggerActiveSchema':
        break;
      case 'contestTrigger':
        formData.append(
          key,
          JSON.stringify(active === 'v1' ? value : createEmptyContestTriggerValues()),
        );
        break;
      case 'contestTriggerV2': {
        if (active === 'v2') {
          const v = value as ContestTriggerV2FormValues;
          formData.append(
            key,
            JSON.stringify({
              ...v,
              triggerData: buildTriggerDataFromV2Episodes(v.episodes),
              amounts: buildAmountsJsonFromV2Form(v.rewardType, v.rewardAmount),
            }),
          );
        } else {
          formData.append(key, JSON.stringify(createEmptyContestTriggerV2Values()));
        }
        break;
      }
      case 'prize':
        formData.append(key, JSON.stringify(value));
        break;
      case 'newCoverImage':
        if (value) {
          const fileInputValue = value as FileInputValue;
          formData.append('image', fileInputValue.file, fileInputValue.fileName);
        }
        break;
      case 'imageUrl':
        if (!values.newCoverImage) {
          formData.append(key, value as string);
        }
        break;
      case 'newCardImage':
        if (value) {
          const fileInputValue = value as FileInputValue;
          formData.append('cardImage', fileInputValue.file, fileInputValue.fileName);
        }
        break;
      case 'newModalImage':
        if (value) {
          const fileInputValue = value as FileInputValue;
          formData.append('modalImage', fileInputValue.file, fileInputValue.fileName);
        }
        break;
      // These are derived read-only URLs returned by the API; the server reconstructs
      // them from the stored imageObject columns. Never sent back to the API.
      case 'cardImageUrl':
      case 'detailImageUrl':
        break;
      // Ring colour, three states:
      //   field absent   → server leaves column unchanged (operator never touched it)
      //   field = ''     → server CLEARS column to NULL (operator explicitly removed colour)
      //   field = '#RGB' → server sets and validates the colour
      // ringColorTouched is the discriminator between the first two cases.
      case 'ringColor':
        if (values.ringColorTouched) {
          // Operator interacted — always send the current value.
          // ''  → clears the column; '#RRGGBB' → sets it.
          formData.append('ringColor', typeof value === 'string' ? value : '');
        }
        // ringColorTouched === false: field absent from FormData → server leaves column as-is
        break;
      case 'ringColorTouched':
        break;
      // Image removal, two states:
      //   field absent → server leaves column unchanged (flag = false)
      //   field = "1"  → server deletes S3 object and clears DB column (flag = true)
      // Any other value would be a 422, but the UI only ever writes boolean true/false.
      case 'removeCardImage':
        if (value === true) {
          formData.append('removeCardImage', '1');
        }
        break;
      case 'removeModalImage':
        if (value === true) {
          formData.append('removeModalImage', '1');
        }
        break;
      default:
        formData.append(key, value as string);
    }
  });
  return formData;
}

interface EventFormProps {
  eventFormValues: EventFormValues
  isLoading: boolean
  onSubmit: (event: EventFormValues) => void
  submitButtonText: string
}

export function EventForm(props: EventFormProps) {
  const {
    eventFormValues,
    isLoading,
    onSubmit,
    submitButtonText,
  } = props;

  const [showImageWarning, setShowImageWarning] = useState(false);
  const [pendingRemoval, setPendingRemoval] = useState<'card' | 'modal' | null>(null);

  const {
    handleSubmit,
    handleChange,
    values,
    errors,
    touched,
    setFieldValue,
    setValues,
    submitCount,
    isValid,
  } = useFormik<EventFormValues>({
    enableReinitialize: true,
    validationSchema: schema,
    initialValues: eventFormValues,
    onSubmit: (submitValues) => {
      // An image counts as "present" if there is a URL, a pending upload, OR the operator
      // deliberately removed it (removeXxx = true). Deliberately removed means the operator
      // confirmed the action, so it does not trigger another warning.
      // A genuinely absent image (never uploaded and not deliberately removed) still triggers
      // the warning so the operator can decide whether to proceed without artwork.
      const hasCardImage = !!(submitValues.cardImageUrl || submitValues.newCardImage)
        || submitValues.removeCardImage;
      const hasModalImage = !!(submitValues.detailImageUrl || submitValues.newModalImage)
        || submitValues.removeModalImage;
      if (!hasCardImage || !hasModalImage) {
        setShowImageWarning(true);
        return;
      }
      onSubmit(submitValues);
    },
  });

  const handleImageChange = (fileInputValue: FileInputValue, dataURL: string) => {
    setValues((prevValues) => ({
      ...prevValues,
      imageUrl: dataURL,
      newCoverImage: fileInputValue,
    }));
  };

  const handleCardImageChange = (fileInputValue: FileInputValue, dataURL: string) => {
    setValues((prevValues) => ({
      ...prevValues,
      cardImageUrl: dataURL,
      newCardImage: fileInputValue,
      // A confirmed upload clears the removal flag: a file and a removal flag for the same image would be rejected with 422.
      removeCardImage: false,
    }));
  };

  const handleModalImageChange = (fileInputValue: FileInputValue, dataURL: string) => {
    setValues((prevValues) => ({
      ...prevValues,
      detailImageUrl: dataURL,
      newModalImage: fileInputValue,
      removeModalImage: false,
    }));
  };

  const handleRemoveCardImageRequest = () => setPendingRemoval('card');
  const handleRemoveModalImageRequest = () => setPendingRemoval('modal');
  const handleRemovalCancel = () => setPendingRemoval(null);
  const handleRemovalConfirm = () => {
    if (pendingRemoval === 'card') {
      // Clear the URL so ImageInput shows no thumbnail; clear newCardImage so no
      // file is appended alongside removeCardImage (conflict → 422).
      setValues((prev) => ({
        ...prev,
        cardImageUrl: '',
        newCardImage: undefined,
        removeCardImage: true,
      }));
    } else if (pendingRemoval === 'modal') {
      setValues((prev) => ({
        ...prev,
        detailImageUrl: '',
        newModalImage: undefined,
        removeModalImage: true,
      }));
    }
    setPendingRemoval(null);
  };

  // Scroll to the first invalid field when a submit attempt fails.
  // Watches submitCount only so the scroll fires once per attempt, not on
  // every keystroke while the operator fixes errors.
  useEffect(() => {
    if (submitCount === 0) return;
    if (isValid) return;
    // document.querySelector is safe here: one form per page, no concurrent mounts
    const firstInvalid = document.querySelector('.is-invalid');
    if (firstInvalid) {
      firstInvalid.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submitCount]);

  const handleSaveAnyway = () => {
    setShowImageWarning(false);
    onSubmit(values);
  };

  const buildImageWarningLines = (): string[] => {
    const lines: string[] = [];
    // Deliberately removed images do not trigger a warning — the operator already confirmed
    // the removal and the confirmation modal explained the default-artwork fallback.
    if (!values.cardImageUrl && !values.newCardImage && !values.removeCardImage) {
      lines.push('Without a card icon, the shelf will crop your existing landscape banner into an oval shape.');
    }
    if (!values.detailImageUrl && !values.newModalImage && !values.removeModalImage) {
      lines.push('Without a modal cover, the popup will show that same banner, cropped to fill the tall frame.');
    }
    return lines;
  };

  const handleRingColorChange = (ringColor: string) => {
    setValues((prev) => ({
      ...prev,
      ringColor,
      ringColorTouched: true,
    }));
  };

  return (
    <Form noValidate onSubmit={handleSubmit}>
      <Row className="mb-3">
        <Col md={12}>
          <TextField
            label="Event Title"
            name="title"
            placeholder="COMPETITION STARTS!"
            value={values.title}
            onChange={handleChange}
            error={touched.title ? errors.title : ''}
            maxLength={titleMaxLength}
          />
        </Col>
        <Col md={12}>
          <TextareaFieldInput
            rows={3}
            label="Event Description"
            placeholder="Our Vampire Insiders will already be fans of Anne..."
            name="description"
            value={values.description}
            onChange={handleChange}
            error={touched.description ? errors.description : ''}
            maxLength={descriptionMaxLength}
          />
        </Col>
      </Row>

      <Row className="mb-3">
        <Col md={6}>
          <TextField
            label="Event Label"
            name="label"
            placeholder="Label to display on event card (for example FEATURED)"
            value={values.label}
            onChange={handleChange}
            error={touched.label ? errors.label : ''}
            maxLength={labelMaxLength}
          />
        </Col>
        <Col md={6}>
          <TextField
            label="Author's Name"
            name="authorLabel"
            placeholder="author"
            value={values.authorLabel}
            onChange={handleChange}
            error={touched.authorLabel ? errors.authorLabel : ''}
            maxLength={authorLabelMaxLength}
          />
        </Col>
      </Row>

      <Row className="mb-3">
        <Col md={3}>
          <NumberField
            label="Order (bigger number - higher in the list)"
            name="priority"
            placeholder="order of events - events with bigger value shows first"
            min="1"
            step="1"
            value={values.priority}
            onChange={handleChange}
          />
        </Col>
        <Col md={6}>
          <TextField
            label="Button Text"
            name="buttonText"
            placeholder="Text to be displayed inside CTA button"
            value={values.buttonText}
            onChange={handleChange}
            error={touched.buttonText ? errors.buttonText : ''}
            maxLength={buttonTextMaxLength}
          />
        </Col>
        <Col md={3}>
          <TextField
            label="Event Card Color"
            name="colors"
            placeholder="Color in format of #abc or linear-gradient(#abc, #def)"
            value={values.colors}
            onChange={handleChange}
            error={touched.colors ? errors.colors : ''}
            maxLength={colorsMaxLength}
          />
        </Col>
      </Row>

      <Row className="mb-3">
        <Col md={4}>
          <DateTimeField
            label="Start Time"
            value={values.startTime}
            onChange={(date: Date | null) => setFieldValue('startTime', date)}
            minDateTime={new Date()}
            error={touched.startTime ? errors.startTime : ''}
          />
        </Col>
        <Col md={4}>
          <DateTimeField
            label="End Time"
            value={values.endTime}
            onChange={(date: Date | null) => setFieldValue('endTime', date)}
            minDateTime={getEarliestEndTime(new Date(values.startTime))}
            error={touched.endTime ? errors.endTime : ''}
          />
        </Col>
        <Col md={4}>
          <TextField
            label="Rules"
            name="specialRequirements"
            placeholder="Rules of event"
            value={values.specialRequirements}
            onChange={handleChange}
            error={touched.specialRequirements ? errors.specialRequirements : ''}
            maxLength={specialRequirementsMaxLength}
          />
        </Col>
      </Row>

      <Row className="mb-3">
        <Col md={6}>

          <Form.Group>
            <Form.Label>Button type</Form.Label>
            <Form.Control
              value={values.buttonAction}
              as="select"
              required
              name="buttonAction"
              onChange={handleChange}
              isInvalid={!!errors.buttonAction}
            >
              <option value={ButtonAction.Story}>Story</option>
              <option value={ButtonAction.Chat}>Chat</option>

            </Form.Control>
          </Form.Group>
        </Col>
        <Col md={6}>
          <ButtonTargetInput
            buttonAction={values.buttonAction}
            value={values.buttonTarget}
            onChange={(buttonTarget: string) => setFieldValue('buttonTarget', buttonTarget)}
            error={touched.buttonTarget ? errors.buttonTarget : ''}
          />
        </Col>
      </Row>

      <Row className="mb-3">
        <Col md={6}>
          <Form.Group>
            <Form.Label>Cover Prize Type</Form.Label>
            <Form.Control
              value={values.prize?.prizeType ?? ''}
              as="select"
              name="prize.prizeType"
              onChange={handleChange}
            >
              {Object.entries(PrizeType)
                .map(([key, value]) => (
                  <option key={key} value={value}>{value !== PrizeType.None ? value : 'None'}</option>
                ))}
            </Form.Control>
          </Form.Group>
        </Col>
        <Col md={6}>
          <TextField
            label="Cover Prize value"
            name="prize.prizeValue"
            placeholder="Prize value - can be any text"
            value={values.prize?.prizeValue ?? ''}
            onChange={handleChange}
            error={touched.prize ? errors.prize : ''}
            maxLength={maxPrizeValueLength}
          />
        </Col>
      </Row>

      <Row className="mb-3">
        <Form.Group as={Col} md="4">
          <Form.Check
            label="Active"
            name="active"
            checked={values.active}
            onChange={handleChange}
          />
        </Form.Group>
      </Row>
      <Card className="my-4 border-secondary">
        <Card.Header className="d-flex align-items-center gap-2 py-2 bg-light">
          <strong className="text-dark">Event Shelf</strong>
          <span className="text-muted" style={{ fontSize: '0.875rem' }}>
            (optional) — how this event looks on the shelf and in the detail popup
          </span>
        </Card.Header>
        <Card.Body>
      <Row className="mb-3">
        <Col md={6}>
          <ImageInput
            label="Card Icon"
            labelBadge={(
              <BadgeWithTooltip
                id="info-card-icon"
                title={CARD_ICON_INFO.title}
                content={CARD_ICON_INFO.content}
                placement="right"
              />
            )}
            initialURL={values.cardImageUrl ?? ''}
            onConfirm={handleCardImageChange}
            error={touched.cardImageUrl ? errors.cardImageUrl : ''}
            imageCropperProps={{
              // Ratio matches EventOval in dorian-lib-creation-tools-ui EventShelfCard.tsx: 21:23.5 (≈ 0.894)
              aspectRatio: 21 / 23.5,
              // Ellipse preview: art is masked to an oval in the app
              variant: Variant.circle,
            }}
            getBlobOptions={{
              // Server (sharp) converts to WebP q80 and stores at 512×576
              croppedImageType: 'image/jpeg',
              maxWidth: 1024,
              maxHeight: 1152,
              imageSmoothingQuality: 'high',
            }}
            previewVariant="oval"
            // Hidden while an upload is pending: upload + remove in one request is rejected with 422.
            onRemoveRequest={values.cardImageUrl && !values.newCardImage
              ? handleRemoveCardImageRequest
              : undefined}
            accept="image/*"
          />
        </Col>
        <Col md={6}>
          <ImageInput
            label="Modal Cover"
            labelBadge={(
              <BadgeWithTooltip
                id="info-modal-cover"
                title={MODAL_COVER_INFO.title}
                content={MODAL_COVER_INFO.content}
                placement="right"
              />
            )}
            initialURL={values.detailImageUrl ?? ''}
            onConfirm={handleModalImageChange}
            error={touched.detailImageUrl ? errors.detailImageUrl : ''}
            imageCropperProps={{
              // 9:16 portrait — stored 1125×2000 by the server
              aspectRatio: 1080 / 1920,
            }}
            getBlobOptions={{
              croppedImageType: 'image/jpeg',
              maxWidth: 2250,
              maxHeight: 4000,
              imageSmoothingQuality: 'high',
            }}
            previewVariant="compact"
            onRemoveRequest={values.detailImageUrl && !values.newModalImage
              ? handleRemoveModalImageRequest
              : undefined}
            accept="image/*"
          />
        </Col>
      </Row>

      <Row className="mb-3">
        <Col md={6}>
          <RingColorPicker
            value={values.ringColor}
            onChange={handleRingColorChange}
            error={errors.ringColor}
          />
        </Col>
      </Row>
        </Card.Body>
      </Card>

      <Row className="mb-3">
        <Col md={6}>
          <ImageInput
            initialURL={values.imageUrl}
            onConfirm={handleImageChange}
            error={touched.imageUrl ? errors.imageUrl : ''}
            imageCropperProps={{
              aspectRatio: 21 / 10,
            }}
            getBlobOptions={{
              croppedImageType: 'image/jpeg', // jpeg used here to make images files smaller size
              maxWidth: 3220,
              maxHeight: 1534,
              imageSmoothingQuality: 'high',
            }}
            accept="image/*"
          />
        </Col>
      </Row>
      <DetailedPrizes
        detailedPrizes={values.detailedEventPrizes}
        onChange={(detailedEventPrizes) => setFieldValue('detailedEventPrizes', detailedEventPrizes)}
        errors={errors.detailedEventPrizes as DetailedPrizesProps['errors']}
        touched={touched.detailedEventPrizes}
      />

      <ContestTriggerSection
        key={values.id ?? 'new-event'}
        valuesV1={values.contestTrigger}
        errorsV1={errors.contestTrigger}
        touchedV1={touched.contestTrigger}
        valuesV2={values.contestTriggerV2}
        errorsV2={errors.contestTriggerV2}
        touchedV2={touched.contestTriggerV2}
        activeSchema={values.contestTriggerActiveSchema}
        onActiveSchemaChange={(schema) => setFieldValue('contestTriggerActiveSchema', schema)}
        onChange={handleChange}
        setFieldValue={setFieldValue}
        onRewardAmountChangeV1={(amount) => setFieldValue('contestTrigger.rewardAmount', amount)}
        onRemoveContestTrigger={() => {
          setFieldValue('contestTrigger', createEmptyContestTriggerValues());
          setFieldValue('contestTriggerV2', createEmptyContestTriggerV2Values());
          setFieldValue('contestTriggerActiveSchema', 'v1');
        }}
      />

      {submitCount > 0 && !isValid && (
        <Alert variant="danger" className="mt-3 mb-2">
          Please fix the errors highlighted above before saving.
        </Alert>
      )}
      <ConfirmModal
        show={showImageWarning}
        centered
        title="Images not uploaded yet"
        confirmButtonTitle="Save anyway"
        cancelButtonTitle="Keep editing"
        onConfirm={handleSaveAnyway}
        onCancel={() => setShowImageWarning(false)}
        onHide={() => setShowImageWarning(false)}
        isLoading={false}
      >
        {buildImageWarningLines().map((line) => (
          <p key={line}>{line}</p>
        ))}
      </ConfirmModal>
      {/* One modal serves both removal confirmations. */}
      <ConfirmModal
        show={pendingRemoval !== null}
        centered
        title={pendingRemoval === 'card' ? 'Remove card icon?' : 'Remove modal cover?'}
        confirmButtonTitle="Remove image"
        cancelButtonTitle="Keep image"
        onConfirm={handleRemovalConfirm}
        onCancel={handleRemovalCancel}
        onHide={handleRemovalCancel}
        isLoading={false}
      >
        {pendingRemoval === 'card' ? (
          <>
            <p>
              Removing the card icon is permanent and takes effect when you save.
              Without a card icon, the shelf will crop your existing landscape banner
              into an oval shape.
            </p>
            <p><strong>This cannot be undone.</strong></p>
          </>
        ) : (
          <>
            <p>
              Removing the modal cover is permanent and takes effect when you save.
              Without a modal cover, the popup will show that same banner, cropped to fill the
              tall frame.
            </p>
            <p><strong>This cannot be undone.</strong></p>
          </>
        )}
      </ConfirmModal>
      <Button type="submit">
        {isLoading && (
          <Spinner
            size="sm"
            animation="border"
          />
        )}
        {submitButtonText}
      </Button>
    </Form>
  );
}
